import { supabaseClient } from 'supabase'
import { Campaign } from './types'

export interface CampaignWithImpressions extends Campaign {
  total_impressions: number
  advertisers?: {
    id: string
    name: string
    category: string
    contact_email: string
    logo_url: string | null
  }
  campaign_creatives?: { id: string }[]
}

interface ImpressionTotalRow {
  campaign_id: string
  total_impressions: number
}

/**
 * Get all campaigns with their impression counts.
 *
 * Impressions are counted in Postgres by `get_campaign_impression_totals`, not
 * by fetching rows — PostgREST caps any response at 1000 rows, so counting
 * client-side made every campaign's total freeze once the *combined* row count
 * across all campaigns crossed that cap.
 */
export async function getCampaignsWithImpressions(): Promise<CampaignWithImpressions[]> {
  // First, get all campaigns with advertiser info
  const { data: campaigns, error: campaignsError } = await supabaseClient
    .from('campaigns')
    .select(`
      *,
      advertisers (
        id,
        name,
        category,
        contact_email,
        logo_url
      ),
      campaign_creatives (
        id
      )
    `)
    .order('created_at', { ascending: false })

  if (campaignsError) {
    console.error('Error fetching campaigns:', campaignsError)
    throw new Error(campaignsError.message)
  }

  if (!campaigns || campaigns.length === 0) {
    return []
  }

  // One aggregated row per campaign that has any impressions
  const { data: totals, error: impressionsError } = await supabaseClient
    .rpc('get_campaign_impression_totals')

  if (impressionsError) {
    console.error('Error fetching impressions:', impressionsError)
    throw new Error(impressionsError.message)
  }

  const impressionCounts = ((totals || []) as ImpressionTotalRow[]).reduce((acc, row) => {
    acc[row.campaign_id] = Number(row.total_impressions) || 0
    return acc
  }, {} as Record<string, number>)

  // Merge campaigns with impression counts
  const campaignsWithImpressions: CampaignWithImpressions[] = campaigns.map(campaign => ({
    ...campaign,
    total_impressions: impressionCounts[campaign.id] || 0
  }))

  return campaignsWithImpressions
}

/**
 * Get impression count for a specific campaign
 * Returns 0 if no impressions found
 */
export async function getCampaignImpressionCount(campaignId: string): Promise<number> {
  const { count, error } = await supabaseClient
    .from('ad_impressions')
    .select('id', { count: 'exact', head: true })
    .eq('campaign_id', campaignId)

  if (error) {
    console.error('Error fetching impression count:', error)
    throw new Error(error.message)
  }

  return count || 0
}

/**
 * The platforms an impression or click can come from. Mirrors the NOT NULL
 * CHECK constraint on `ad_impressions.platform` / `ad_clicks.platform`, so the
 * split below is exhaustive — every logged event lands in one of these buckets.
 */
export const AD_PLATFORMS = ['mobile', 'web'] as const
export type AdPlatform = typeof AD_PLATFORMS[number]

/**
 * A single engagement metric: how many events fired (`total`) and how many
 * distinct users produced them (`unique`). `unique` is always <= `total`.
 */
export interface CountStat {
  total: number
  unique: number
}

/**
 * A metric plus its per-platform split. `total`/`unique` stay the all-platforms
 * figures, so existing readers see no change; `byPlatform` always carries an
 * entry for every AD_PLATFORMS key, zeroed when that platform logged nothing.
 *
 * `total` is the sum of the platform totals, but `unique` is NOT the sum of the
 * platform uniques: someone who saw the ad on both phone and web is one unique
 * overall and one on each platform.
 *
 * Web *click* uniques are structurally always 0 — web clicks are written by a
 * server route that carries neither user_id nor session_id, by the deliberate
 * design recorded in `web/src/lib/adSession.ts`. That is why the admin renders
 * the platform split as totals only.
 */
export interface PlatformCountStat extends CountStat {
  byPlatform: Record<AdPlatform, CountStat>
}

/**
 * Engagement stats for a campaign: impressions plus ad_clicks broken down by
 * `click_type` (open_detail, whatsapp, call, website, instagram, promo_copy,
 * directions), each with total and unique-user counts and a mobile/web split.
 */
export interface CampaignEngagementStats {
  impressions: PlatformCountStat
  totalClicks: PlatformCountStat
  clicksByType: Record<string, PlatformCountStat>
}

// One row of `get_campaign_engagement_stats`. `kind` is 'impression', 'click'
// (one row per click_type) or 'click_total' (all click types combined). Each
// metric appears once with `platform` null (all platforms combined) and once
// per platform that logged anything; the source columns are NOT NULL, so a null
// here is always the combined roll-up and never missing data.
interface EngagementStatRow {
  kind: 'impression' | 'click' | 'click_total'
  click_type: string | null
  platform: string | null
  total: number
  unique_users: number
}

// Fresh objects per call — these are mutated while folding the rows, so a
// shared constant would leak one campaign's numbers into the next.
const zeroStat = (): CountStat => ({ total: 0, unique: 0 })
const zeroPlatformStat = (): PlatformCountStat => ({
  total: 0,
  unique: 0,
  byPlatform: { mobile: zeroStat(), web: zeroStat() }
})

const isAdPlatform = (value: string): value is AdPlatform =>
  (AD_PLATFORMS as readonly string[]).includes(value)

/**
 * Get engagement stats (impressions + per-click-type clicks) for one campaign.
 *
 * Counted in Postgres by `get_campaign_engagement_stats`. This used to fetch
 * every impression and click row and reduce them in JS, which broke silently
 * against PostgREST's 1000-row response cap: totals stuck at exactly 1000 and
 * unique-user counts were derived from an arbitrary 1000-row sample.
 *
 * A user is identified by user_id when present, otherwise by the anonymous
 * session_id; a row with neither counts toward `total` but not `unique`, which
 * is what COALESCE(user_id, session_id) inside COUNT(DISTINCT ...) gives us.
 */
export async function getCampaignEngagementStats(campaignId: string): Promise<CampaignEngagementStats> {
  const { data, error } = await supabaseClient
    .rpc('get_campaign_engagement_stats', { p_campaign_id: campaignId })

  if (error) {
    console.error('Error fetching engagement stats:', error)
    throw new Error(error.message)
  }

  const rows = (data || []) as EngagementStatRow[]
  const statOf = (row: EngagementStatRow): CountStat => ({
    total: Number(row.total) || 0,
    unique: Number(row.unique_users) || 0
  })

  const impressions = zeroPlatformStat()
  const totalClicks = zeroPlatformStat()
  const clicksByType: Record<string, PlatformCountStat> = {}

  // Which metric a row belongs to. Rows arrive in no guaranteed order, so a
  // per-click-type bucket is created on first sight from either row shape.
  const bucketFor = (row: EngagementStatRow): PlatformCountStat | null => {
    if (row.kind === 'impression') return impressions
    if (row.kind === 'click_total') return totalClicks
    if (!row.click_type) return null
    if (!clicksByType[row.click_type]) {
      clicksByType[row.click_type] = zeroPlatformStat()
    }
    return clicksByType[row.click_type]
  }

  for (const row of rows) {
    const bucket = bucketFor(row)
    if (!bucket) continue

    const stat = statOf(row)
    if (row.platform === null) {
      // The all-platforms roll-up row.
      bucket.total = stat.total
      bucket.unique = stat.unique
    } else if (isAdPlatform(row.platform)) {
      bucket.byPlatform[row.platform] = stat
    }
    // A platform the UI does not know about is ignored here but still counted
    // in the roll-up row above, so totals stay correct if one is ever added.
  }

  return { impressions, totalClicks, clicksByType }
}
